import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useBoolean } from 'react-hanger';
import { useMutation } from '@apollo/client/react';
import { CompositeFilterDescriptor, orderBy } from '@progress/kendo-data-query';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import {
    GridHeaderSelectionChangeEvent,
    GridSelectionChangeEvent,
    GridFilterChangeEvent,
} from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import style from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/style.module.scss';
import { ExtendedCompleteRiskFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    assignCmControlChartsToRisk,
    assignCmControlChartsToRiskVariables,
    getControlChartsForRisk_OpraQueries_getControlChartsForRisk_Panel,
} from '@queries/generated/risk-assessment.generated';
import { ASSIGN_CM_CONTROLS, GET_COMPLETE_RISKS_RISK_LOG } from '@queries/risk-assessment';

interface AssignCmControlChartsToRiskDialogProps {
    assignableCmControlCharts?: (getControlChartsForRisk_OpraQueries_getControlChartsForRisk_Panel | null)[];
    onClose: () => void;
    onSubmit: (selectedCtqIds: string[], close: boolean) => void;
    risk: ExtendedCompleteRiskFields;
}

type SelectState = SelectDescriptor;

const AssignCmControlChartsToRiskDialog = ({
    assignableCmControlCharts,
    onClose,
    onSubmit,
    risk,
}: AssignCmControlChartsToRiskDialogProps) => {
    const { t: tDialog } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs' });
    const { t: tCommon } = useTranslation('common');

    const { TooltipCell } = createGridCustomCells();

    const [executeAssignCmControlsToRisk, { loading: assigningCmControls }] = useMutation<
        assignCmControlChartsToRisk,
        assignCmControlChartsToRiskVariables
    >(ASSIGN_CM_CONTROLS);

    const {
        value: clearSelectedRows,
        setTrue: setClearSelectedRowsTrue,
        setFalse: setClearSelectedRowsFalse,
    } = useBoolean(false);

    const [select, setSelect] = useState<SelectState>({});
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor>({
        logic: 'and',
        filters: [],
    });

    const rows = useMemo(
        () =>
            assignableCmControlCharts
                ? orderBy(
                      assignableCmControlCharts.map((item) => {
                          return {
                              ...item,
                              plotTitle: {
                                  active: true,
                                  name: item?.name,
                              },
                          };
                      }),
                      [{ field: 'plotTitle.name', dir: 'asc' }],
                  )
                : [],
        [assignableCmControlCharts],
    );

    useEffect(() => {
        setClearSelectedRowsTrue();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setSelect({});
    }, [rows, setClearSelectedRowsTrue]);

    const onSelectionChange = useCallback(
        (event: GridSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const onHeaderSelectionChange = useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const onSubmitAssign = async (close: boolean) => {
        if (!rows.length) return;

        const selectedRowKeys = Object.keys(select).filter((k) => select[k] === true);
        if (!selectedRowKeys.length) return;

        const selectedCmControlsIds =
            rows
                .filter((r) => r != null)
                .filter((r) => selectedRowKeys.includes(r.id.toString()))
                .map((r) => r.id)
                .filter((p) => p !== undefined)
                .filter((p) => p !== null) ?? [];

        if (!risk || !risk.studyId || !selectedCmControlsIds.length) return;
        const alreadyAssignedIds = risk.associatedControlsHubPanelIds ?? [];
        const mergedIds = Array.from(new Set([...alreadyAssignedIds, ...selectedCmControlsIds]));

        try {
            const response = await executeAssignCmControlsToRisk({
                variables: {
                    id: risk.id,
                    cmControlChartsIds: mergedIds,
                },
                refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId: risk.studyId } }],
            });

            if (!response?.data?.assignCmControlCharts) {
                ShowError(tCommon('errorMessage'));
                return;
            }

            ShowSuccess(tCommon('actionsSuccessfulToast'));
            setFilterState({
                logic: 'and',
                filters: [],
            });
            setSelect({});
            setClearSelectedRowsTrue();
            onSubmit(selectedCmControlsIds, close);
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const selectedCount = Object.keys(select).filter((k) => select[k] === true).length;
    const formActionsEnabled = selectedCount > 0 && !assigningCmControls;
    const dialogTitle = `${tDialog('assignCmChartsToRisk.title')}`;

    const onCloseHandler = () => {
        onClose();
    };

    const columns: GridColumnProps[] = [
        {
            id: 'checkbox',
            field: 'checkbox',
            columnType: 'checkbox',
            className: 'checkboxSelection',
            width: 40,
            customColumn: true,
            resizable: false,
            locked: true,
            filterable: false,
        },
        {
            id: 'name',
            field: 'name',
            title: tDialog('assignCmChartsToRisk.columns.plotTitle'),
            lockedByDefault: true,
            width: 1,
            minWidth: 250,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            width: 1,
            minWidth: 250,
            id: 'xAxisLabel',
            field: 'xAxisLabel',
            title: tDialog('assignCmChartsToRisk.columns.xAxisLabel'),
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            width: 1,
            minWidth: 250,
            id: 'yAxisLabel',
            field: 'yAxisLabel',
            title: tDialog('assignCmChartsToRisk.columns.yAxisLabel'),
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
    ];

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="assign-cm-control-charts-to-risk-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSubmitAssign(false)}
            primaryBtnLabel={tDialog('assignCmChartsToRisk.buttons.assign')}
            primaryBtnDisabled={!formActionsEnabled || assigningCmControls}
            onAdditionalAction={() => onSubmitAssign(true)}
            additionalBtnLabel={tDialog('assignCmChartsToRisk.buttons.assignAndClose')}
            additionalBtnDisabled={!formActionsEnabled || assigningCmControls}
            initialHeight={520}
            initialWidth={722}
            height={null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="k-w-full k-h-full">
                {risk.statement && (
                    <div>
                        {risk.statement.length <= 600 ? (
                            <>
                                <span className="k-font-medium">{tDialog('assignCmChartsToRisk.statement')} - </span>
                                <span style={{ wordBreak: 'break-word' }}>{risk.statement}</span>
                            </>
                        ) : (
                            <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                                <span className="k-font-medium">{tDialog('assignCmChartsToRisk.statement')} - </span>
                                <span
                                    style={{ wordBreak: 'break-word' }}
                                    title={risk.statement}
                                >{`${risk.statement.slice(0, 600)}...`}</span>
                            </Tooltip>
                        )}
                    </div>
                )}
                <div style={{ height: '300px', paddingBottom: '0.25rem' }}>
                    <Grid
                        excludeHiddenRowMenuActions
                        title={tDialog('assignCmChartsToRisk.description')}
                        maxHeight="auto"
                        testDataId="assignCmChartsToRisk-grid"
                        data={rows}
                        columns={columns}
                        filterable
                        loading={assigningCmControls}
                        useLoadingOverlay
                        showAddIcon={false}
                        scrollable="virtual"
                        dataItemKey="id"
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: 'multiple',
                        }}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                        onSelectionChange={onSelectionChange}
                        clearGridSelectedRows={clearSelectedRows}
                        useCardWrapper={false}
                        filter={filterState}
                        onFilterChange={async (e: GridFilterChangeEvent) => {
                            const next = (e.filter || { logic: 'and', filters: [] }) as CompositeFilterDescriptor;
                            setFilterState(next);
                            setClearSelectedRowsTrue();
                            setSelect({});
                            return true;
                        }}
                        clearFilter={async () => {
                            setFilterState({ logic: 'and', filters: [] });
                            setClearSelectedRowsTrue();
                            setSelect({});
                            return true;
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AssignCmControlChartsToRiskDialog;
